import { useEffect } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import SEOHead from "../../components/feature/SEOHead";
import { useTheme } from "../../contexts/ThemeContext";

type Status = "shipped" | "in-progress" | "planned";

interface ChangelogEntry {
  version?: string;
  date: string;
  title: string;
  badge: Status;
  description: string;
  items: string[];
}

const BADGE_CONFIG: Record<Status, { label: string; classes: { dark: string; light: string } }> = {
  shipped: {
    label: "Shipped",
    classes: {
      dark: "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20",
      light: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    },
  },
  "in-progress": {
    label: "In Progress",
    classes: {
      dark: "bg-amber-500/10 text-amber-400 border border-amber-500/20",
      light: "bg-amber-50 text-amber-700 border border-amber-200",
    },
  },
  planned: {
    label: "Planned",
    classes: {
      dark: "bg-white/5 text-[#888] border border-white/10",
      light: "bg-black/4 text-[#777] border border-black/8",
    },
  },
};

const ENTRIES: ChangelogEntry[] = [
  {
    version: "0.9.0",
    date: "March 2026",
    title: "Beta Launch",
    badge: "shipped",
    description: "The first public version of Trackly is live. Built from the ground up for resellers who are serious about their margins.",
    items: [
      "Profit calculator with fee-aware math for eBay, StockX, GOAT, and more",
      "Flip history log with sell-through tracking",
      "Performance stats dashboard with ROI and margin summaries",
      "Email-based 2FA for account security",
      "Dark & light mode across all pages",
      "Early Access waitlist with referral system",
    ],
  },
  {
    version: "0.8.0",
    date: "February 2026",
    title: "Private Alpha",
    badge: "shipped",
    description: "Closed testing with a small group of resellers. Core calculation engine validated against real-world sales data.",
    items: [
      "Core fee engine supporting 8 platforms",
      "User profile and avatar upload",
      "Basic flip entry form with platform picker",
      "Session management and auto-logout",
      "Onboarding flow for new users",
    ],
  },
  {
    version: "1.0.0",
    date: "Q2 2026",
    title: "Full Public Release",
    badge: "in-progress",
    description: "The full v1 launch — removing all beta gates, polishing every screen, and opening sign-ups to everyone.",
    items: [
      "Open sign-ups, no waitlist required",
      "Stripe subscription billing (Pro plan)",
      "Platform fee auto-sync — no more manual rate updates",
      "CSV export of all flip history",
      "Improved mobile experience",
    ],
  },
  {
    version: "1.1.0",
    date: "Q3 2026",
    title: "Bulk Entry & Tagging",
    badge: "planned",
    description: "Power-user tools for resellers managing high volume. Log multiple flips at once and tag them by category, source, or batch.",
    items: [
      "Bulk flip entry form (up to 50 at once)",
      "Custom tagging and category labels",
      "Filter and search flip history by tag",
      "Batch delete and edit",
    ],
  },
  {
    date: "Q3 2026",
    title: "Platform Integrations",
    badge: "planned",
    description: "Connect your selling accounts directly so Trackly can pull sale data automatically — no more manual entry.",
    items: [
      "eBay Sold Listings sync",
      "StockX order history import",
      "Mercari and Poshmark support",
      "Automatic fee detection per order",
    ],
  },
  {
    date: "Q4 2026",
    title: "Team & Shared Accounts",
    badge: "planned",
    description: "Run your reselling operation with a partner or small team. Share a dashboard, split profits, stay aligned.",
    items: [
      "Multi-user workspaces",
      "Role-based access (owner, editor, viewer)",
      "Shared flip history and profit summaries",
      "Team-level performance analytics",
    ],
  },
];

const SITE_URL = import.meta.env.VITE_SITE_URL ?? "https://trackly.app";

const changelogJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Trackly Changelog — Product Updates & Roadmap",
  "description": "See what's shipped, what's in progress, and what's planned for Trackly. Follow along as we build the best profit tracker for resellers.",
  "url": `${SITE_URL}/changelog`,
  "isPartOf": { "@type": "WebSite", "name": "Trackly", "url": SITE_URL },
};

function StatusBadge({ status, isDark }: { status: Status; isDark: boolean }) {
  const cfg = BADGE_CONFIG[status];
  return (
    <span className={`inline-flex items-center gap-1.5 font-inter text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${isDark ? cfg.classes.dark : cfg.classes.light}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
        status === "shipped" ? "bg-emerald-500" : status === "in-progress" ? "bg-amber-500" : isDark ? "bg-[#555]" : "bg-[#bbb]"
      }`} />
      {cfg.label}
    </span>
  );
}

export default function Changelog() {
  const { isDark } = useTheme();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? "bg-[#050505]" : "bg-[#f8f8f8]"}`}>
      <SEOHead
        title="Trackly Changelog — Product Updates & Roadmap"
        description="See what's shipped, what's in progress, and what's planned for Trackly. Follow along as we build the best profit tracker for resellers."
        keywords="trackly changelog, reseller app updates, profit tracker roadmap, trackly new features"
        canonical={`${SITE_URL}/changelog`}
        jsonLd={changelogJsonLd}
      />
      <Navbar />

      <main className="pt-32 pb-24 px-8 lg:px-16">
        <div className="max-w-2xl mx-auto">

          {/* Header */}
          <div className="mb-16">
            <span className={`inline-flex items-center gap-2 font-inter text-xs font-medium px-3 py-1.5 rounded-full mb-6 ${isDark ? "bg-white/5 text-[#888]" : "bg-black/6 text-[#666]"}`}>
              <i className="ri-git-commit-line text-xs" />
              Product Updates
            </span>
            <h1 className={`font-syne text-4xl font-bold mb-5 leading-tight ${isDark ? "text-white" : "text-[#111111]"}`}>
              Changelog
            </h1>
            <p className={`font-inter text-base leading-relaxed ${isDark ? "text-[#666666]" : "text-[#555555]"}`}>
              What we&apos;ve shipped, what we&apos;re building, and what&apos;s on the horizon. Trackly is early — this page grows as we do.
            </p>
          </div>

          {/* Legend */}
          <div className={`flex items-center gap-4 flex-wrap mb-12 p-4 rounded-xl border ${isDark ? "bg-white/[0.02] border-white/6" : "bg-white border-black/8"}`}>
            <span className={`font-inter text-xs ${isDark ? "text-[#555]" : "text-[#aaa]"}`}>Status key</span>
            <div className="flex items-center gap-4 flex-wrap">
              {(Object.keys(BADGE_CONFIG) as Status[]).map((s) => (
                <StatusBadge key={s} status={s} isDark={isDark} />
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* vertical line */}
            <div className={`absolute left-3.5 top-0 bottom-0 w-px ${isDark ? "bg-white/6" : "bg-black/8"}`} />

            <div className="flex flex-col gap-10">
              {ENTRIES.map((entry, i) => (
                <div key={i} className="relative pl-12">
                  {/* dot */}
                  <div className={`absolute left-0 top-1 w-7 h-7 flex items-center justify-center rounded-full border transition-colors duration-300 ${
                    entry.badge === "shipped"
                      ? isDark ? "bg-emerald-500/10 border-emerald-500/30" : "bg-emerald-50 border-emerald-200"
                      : entry.badge === "in-progress"
                      ? isDark ? "bg-amber-500/10 border-amber-500/30" : "bg-amber-50 border-amber-200"
                      : isDark ? "bg-white/4 border-white/10" : "bg-white border-black/10"
                  }`}>
                    <i className={`text-xs ${
                      entry.badge === "shipped"
                        ? `ri-check-line ${isDark ? "text-emerald-400" : "text-emerald-600"}`
                        : entry.badge === "in-progress"
                        ? `ri-loader-4-line ${isDark ? "text-amber-400" : "text-amber-600"}`
                        : `ri-time-line ${isDark ? "text-[#555]" : "text-[#bbb]"}`
                    }`} />
                  </div>

                  {/* Card */}
                  <div className={`rounded-2xl border p-6 transition-colors duration-300 ${isDark ? "bg-white/[0.02] border-white/6" : "bg-white border-black/8"}`}>
                    {/* Card header */}
                    <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h2 className={`font-syne text-base font-semibold ${isDark ? "text-white" : "text-[#111111]"}`}>
                            {entry.title}
                          </h2>
                          {entry.version && (
                            <span className={`font-inter text-xs px-2 py-0.5 rounded font-medium ${isDark ? "bg-white/6 text-[#777]" : "bg-black/5 text-[#888]"}`}>
                              v{entry.version}
                            </span>
                          )}
                        </div>
                        <p className={`font-inter text-xs ${isDark ? "text-[#555]" : "text-[#aaa]"}`}>{entry.date}</p>
                      </div>
                      <StatusBadge status={entry.badge} isDark={isDark} />
                    </div>

                    <p className={`font-inter text-sm leading-relaxed mb-5 ${isDark ? "text-[#666666]" : "text-[#555555]"}`}>
                      {entry.description}
                    </p>

                    <ul className="flex flex-col gap-2.5">
                      {entry.items.map((item) => (
                        <li key={item} className="flex items-start gap-2.5">
                          <div className={`w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5 ${isDark ? "text-[#444]" : "text-[#ccc]"}`}>
                            <i className="ri-arrow-right-s-line text-xs" />
                          </div>
                          <span className={`font-inter text-sm leading-relaxed ${isDark ? "text-[#666]" : "text-[#555]"}`}>
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer nudge */}
          <div className={`mt-16 rounded-2xl border p-8 text-center transition-colors duration-300 ${isDark ? "bg-white/[0.02] border-white/6" : "bg-white border-black/8"}`}>
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl mx-auto mb-5 ${isDark ? "bg-white/6" : "bg-black/5"}`}>
              <i className={`ri-notification-4-line text-base ${isDark ? "text-[#aaa]" : "text-[#555]"}`} />
            </div>
            <h3 className={`font-syne text-base font-semibold mb-2 ${isDark ? "text-white" : "text-[#111111]"}`}>
              Want to follow along?
            </h3>
            <p className={`font-inter text-sm leading-relaxed mb-6 max-w-sm mx-auto ${isDark ? "text-[#666]" : "text-[#555]"}`}>
              Join the early access list and we&apos;ll notify you when new features ship. No spam, just updates.
            </p>
            <a
              href="/"
              className={`inline-flex items-center gap-2 font-inter text-sm font-medium px-5 py-2.5 rounded-full cursor-pointer transition-all duration-200 whitespace-nowrap ${isDark ? "bg-white text-black hover:bg-[#e5e5e5]" : "bg-[#111111] text-white hover:bg-[#333333]"}`}
            >
              <i className="ri-arrow-left-s-line" />
              Join Early Access
            </a>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}
